import { createRouter, createWebHashHistory } from 'vue-router';
import { useSessionStore } from '@/stores/session';

/**
 * Routing for the FRVB Referee Nomination System.
 *
 * - admins land on /admin (Nominate is the default tab)
 * - referees land on /referee (their dashboard + scoresheet flow)
 * - /confirm/:token is the public scoresheet-via-link page
 *
 * Hash-based routing keeps the SPA portable behind any nginx config.
 */
export const router = createRouter({
    history: createWebHashHistory(import.meta.env.BASE_URL),
    routes: [
        { path: '/login',           name: 'login',     component: () => import('./views/LoginView.vue'),
                                    meta: { public: true } },
        // Bare / always sends to the user's natural landing.
        { path: '/',                redirect: () => {
            const session = useSessionStore();
            return session.defaultRoute();
        } },
        { path: '/new',             name: 'new-match', component: () => import('./views/NewMatchView.vue') },
        { path: '/admin',           name: 'admin',     component: () => import('./views/AdminView.vue'),
                                    children: [
            { path: '',             component: () => import('./views/admin/AdminDashboard.vue') },
            { path: 'nominations',  component: () => import('./views/admin/NominationsTab.vue') },
            { path: 'panel',        component: () => import('./views/admin/PanelTab.vue') },
            { path: 'management',   component: () => import('./views/admin/RefereesManagementView.vue') },
            { path: 'stats',        component: () => import('./views/admin/StatsTab.vue') },
            { path: 'matches',      component: () => import('./views/admin/MatchesTab.vue') },
            { path: 'seasons',      component: () => import('./views/admin/SeasonsTab.vue') },
            { path: 'competitions', component: () => import('./views/admin/CompetitionsTab.vue') },
            { path: 'teams',        component: () => import('./views/admin/TeamsTab.vue') },
            { path: 'referees',     component: () => import('./views/admin/RefereesTab.vue') },
            { path: 'users',        component: () => import('./views/admin/UsersTab.vue') },
            { path: 'settings',     component: () => import('./views/admin/SettingsTab.vue') }
        ]},
        { path: '/admin/referees/:id', component: () => import('./views/admin/RefereeProfileView.vue'), props: true },
        // Referee-side dashboard + scoresheet flow.
        { path: '/referee',         name: 'referee',   component: () => import('./views/RefereeDashboardView.vue') },
        { path: '/scoresheet/:nominationId',
                                    name: 'scoresheet',
                                    component: () => import('./views/ScoresheetSubmitView.vue'),
                                    props: true },
        { path: '/confirm/:token',  name: 'confirm',
                                    component: () => import('./views/ScoresheetSubmitView.vue'),
                                    props: true,
                                    meta: { public: true } },
        { path: '/:catchAll(.*)',   redirect: '/' }
    ]
});

// Auth guard — block protected routes until the session has been
// restored from the cookie, then enforce role-based access.
router.beforeEach(async (to) => {
    const session = useSessionStore();
    if (!session.ready) {
        await session.restore();
    }
    if (to.meta?.public) return true;
    if (!session.isAuthed) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (!session.canAccess(to.path)) {
        return session.defaultRoute();
    }
    return true;
});
